import { HTMLAttributes, ReactNode } from "react";
import { Button, Card, Icon, IconVariant, Typography } from "@/components/atoms";
import { clsx } from "@/utils";

export interface SetupPanelItemAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary";
  disabled?: boolean;
}

export interface SetupPanelItemHelpOption {
  label: string;
  icon: string | React.ReactElement;
  onClick: () => void;
}

export interface SetupPanelItemProps extends HTMLAttributes<HTMLDivElement> {
  icon?: string | ReactNode;
  iconBackground?: string;
  isCustomIcon?: boolean;
  iconVariant?: IconVariant;
  title: string;
  isCompleted?: boolean;
  actions?: SetupPanelItemAction[];
  helpOptions?: SetupPanelItemHelpOption[];
  additionalContent?: ReactNode;
}

export const SetupPanelItem = ({
  icon,
  iconBackground,
  iconVariant,
  isCustomIcon = false,
  title,
  isCompleted = false,
  actions,
  helpOptions,
  additionalContent,
  className,
  ...props
}: SetupPanelItemProps) => {
  const renderIcon = () => {
    if (!icon) return null;

    if (typeof icon === "string") {
      return (
        <div className={clsx("flex h-8 w-8 items-center justify-center rounded", iconBackground || "bg-primary-light")}>
          <Icon name={icon} size="md" className="text-primary-dark" isCustom={isCustomIcon} variant={iconVariant} />
        </div>
      );
    }

    return <div className={clsx("flex h-8 w-8 items-center justify-center rounded", iconBackground || "bg-primary-light")}>{icon}</div>;
  };

  return (
    <Card className={clsx("!bg-white !p-0 transition-all duration-200 hover:shadow-sm", className)} {...props}>
      <div className="px-5 py-4">
        {/* Mobile: Vertical layout, Desktop: Horizontal layout */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-0">
          {/* Top section: Icon and title */}
          <div className="flex flex-col gap-2.5 md:flex-row md:items-center md:gap-4">
            <div className="flex items-center gap-4">
              {renderIcon()}
              <div className="flex items-center gap-1.5">
                <Typography variant="action" className="text-neutral-dark-gray">
                  {title}
                </Typography>
                {isCompleted && <Icon name="TickCircle" size="lg" variant="Bold" className="text-success" />}
              </div>
            </div>

            {/* Mobile: Actions below header, Desktop: Actions on the right */}
            {actions && (
              <div className="flex gap-2 md:hidden">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    size="xs"
                    variant={action.variant === "primary" ? "primary" : "secondary"}
                    className="h-8"
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Desktop: Actions on the right */}
          {actions && (
            <div className="hidden gap-2 md:flex">
              {actions.map((action, index) => (
                <Button
                  key={index}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  size="xs"
                  variant={action.variant === "primary" ? "primary" : "secondary"}
                  className="h-8"
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {additionalContent && <div className="mt-4">{additionalContent}</div>}

        {helpOptions && helpOptions.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2 md:gap-6">
            {helpOptions.map((option, index) => (
              <Button
                key={index}
                onClick={option.onClick}
                variant="tertiary-light"
                size="xs"
                endIcon={option.icon}
                className="!p-0 text-neutral-silver-gray hover:bg-white hover:text-neutral-dark-gray"
              >
                {option.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
